/**
 * Which country flag stands in for a language.
 *
 * A language is not a country. This maps each offered language to a single
 * representative ISO 3166-1 country whose flag visually anchors it — the flag
 * is DECORATIVE, and the language's own name always carries the meaning beside
 * it. Languages with no honest single-country flag (constructed, cross-border,
 * or spoken across many states without a home) resolve to `undefined`, and the
 * caller renders no flag rather than an arbitrary one.
 *
 * Lives in `@tripcart/shared` because both storefront and operator need the
 * same answer: traveler-web's language picker and operator-web's Site language
 * picker would otherwise each carry their own copy and drift apart, which is
 * how one surface ends up showing a flag the other does not.
 *
 * Country codes are lower-case and match the self-hosted SVG set each app syncs
 * into its own `public/flags/` (see `scripts/sync-flags.mjs`). Never emoji —
 * Windows/Chromium render Unicode flag emoji as the bare two-letter code, and
 * the design system bans emoji outright (packages/ui/CLAUDE.md §2.10).
 */

/** Base language subtag → representative country. Keyed on the BARE subtag; a
 *  locale that names its own region is handled by `languageFlagCountry`. */
const LANGUAGE_COUNTRY: Readonly<Record<string, string>> = {
  af: "za",
  sq: "al",
  am: "et",
  ar: "sa",
  hy: "am",
  az: "az",
  eu: "es",
  be: "by",
  bn: "bd",
  bs: "ba",
  bg: "bg",
  ca: "es",
  ceb: "ph",
  ny: "mw",
  co: "fr",
  hr: "hr",
  cs: "cz",
  da: "dk",
  nl: "nl",
  en: "gb",
  et: "ee",
  tl: "ph",
  fi: "fi",
  fr: "fr",
  fy: "nl",
  gl: "es",
  ka: "ge",
  de: "de",
  el: "gr",
  gu: "in",
  ht: "ht",
  ha: "ng",
  haw: "us",
  iw: "il",
  he: "il",
  hi: "in",
  hu: "hu",
  is: "is",
  ig: "ng",
  id: "id",
  ga: "ie",
  it: "it",
  ja: "jp",
  jw: "id",
  kn: "in",
  kk: "kz",
  km: "kh",
  ko: "kr",
  ky: "kg",
  lo: "la",
  la: "va",
  lv: "lv",
  lt: "lt",
  lb: "lu",
  mk: "mk",
  mg: "mg",
  ms: "my",
  ml: "in",
  mt: "mt",
  mi: "nz",
  mr: "in",
  mn: "mn",
  my: "mm",
  ne: "np",
  no: "no",
  nb: "no",
  nn: "no",
  ps: "af",
  fa: "ir",
  pl: "pl",
  pt: "pt",
  pa: "in",
  ro: "ro",
  ru: "ru",
  sm: "ws",
  gd: "gb",
  sr: "rs",
  st: "ls",
  sn: "zw",
  sd: "pk",
  si: "lk",
  sk: "sk",
  sl: "si",
  so: "so",
  es: "es",
  su: "id",
  sw: "ke",
  sv: "se",
  tg: "tj",
  ta: "in",
  te: "in",
  th: "th",
  tr: "tr",
  uk: "ua",
  ur: "pk",
  uz: "uz",
  vi: "vn",
  cy: "gb",
  xh: "za",
  yi: "il",
  yo: "ng",
  zu: "za",
  // Written forms that name a script rather than a place; the country below is
  // where that script is the standard written form.
  zh: "cn",
  bo: "cn",
  as: "in",
  or: "in",
  bel: "by",
  arg: "es",
  ary: "ma",
  azb: "ir",
  ckb: "iq",
};

/**
 * The flag to show for a stored language code, or `undefined` when none is
 * honest.
 *
 * Accepts the WordPress core locale codes `SUPPORTED_LANGUAGES` stores
 * (`ne_NP`, `pt_BR`, `de_DE_formal`) as well as bare subtags (`af`) and BCP-47
 * hyphens (`zh-TW`).
 *
 * A locale that names its own region wins: `pt_BR` is Brazil, not Portugal, and
 * `de_AT` is Austria — deferring to the base-language map there would show the
 * wrong flag for exactly the entries an operator is most likely to notice.
 */
export function languageFlagCountry(code: string): string | undefined {
  if (!code) return undefined;

  const parts = code.replace(/-/g, "_").split("_").filter(Boolean);
  const base = parts[0]?.toLowerCase();
  if (!base) return undefined;

  // Only the SECOND subtag is ever read as a region, and only when it is two
  // letters. That pair of conditions is what keeps `de_DE_formal` off a country
  // named "formal" (`formal` is parts[2], never examined) and `zh_Hans` off one
  // named "hans" (four letters, so it falls through to the base map).
  const region = parts[1]?.toLowerCase();
  if (region && /^[a-z]{2}$/.test(region)) return region;

  // `Object.hasOwn`, not a bare index: a base subtag that happens to name an
  // Object.prototype member ("constructor", "toString") would otherwise return
  // a function through a signature that promises `string | undefined`.
  return Object.hasOwn(LANGUAGE_COUNTRY, base)
    ? LANGUAGE_COUNTRY[base]
    : undefined;
}
